import React, {
  FC,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { CALENDAR_MODE } from 'framework/calendar/enums';
import { CalendarProps } from 'framework/calendar/types';
import { useOutsideScroll } from 'framework/dropdown/hooks/useOutsideScroll';

import { EMPTY_ARRAY, EMPTY_FUNCTION } from '../../constants';
import { Field } from '../field';
import { useDebounce } from '../field/hooks/useDebounce';
import { OptionsCalendar } from '../Options';

import { DropdownContext, useDropdownContext } from './context/DropdownContext';
import { useFocus } from './hooks/useFocus';
import { useKeyboardNavigation } from './hooks/useKeyboardNavigation';
import { useOptionsFilter } from './hooks/useOptionsFilter';
import { useOutsideClick } from './hooks/useOutsideClick';
import { DropdownProps } from './types';

export const Container = <T extends Record<string, any>>({
  options = EMPTY_ARRAY,
  option_key = '',
  placeholder = '',
  label = '',
  prefix = '',
  suffix = '',
  information = '',
  padding = '0px',
  height = '32px',
  width = '100%',
  background,
  left_icon,
  right_icon,
  onFocus: onFocusProp = EMPTY_FUNCTION,
  onBlur: onBlurProp = EMPTY_FUNCTION,
  onKeyDown: onKeyDownProp = EMPTY_FUNCTION,
  onSelect: onSelectProp = EMPTY_FUNCTION,
  onChange: onChangeProp = EMPTY_FUNCTION,
  onClick: onClickField = EMPTY_FUNCTION,
  onRemove: onRemoveProp = EMPTY_FUNCTION,
  onMouseDown: onMouseDownProp = EMPTY_FUNCTION,
  value = '',
  extend = false,
  auto_focus = false,
  autosize = false,
  disabled = false,
  error = false,
  debounce = 400,
  loading = false,
  filterable = false,
  keyboard_disabled = false,
  clear = false,
  transparent = false,
  theme,
  children,
}: DropdownProps<T>) => {
  const [is_open, setOpen] = useState(false);
  const [reset_filter, resetFilter] = useState(true);
  const [typed_value, setTypedValue] = useState<string>(value);

  const inputRef = useRef<HTMLInputElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);

  const { is_typing } = useDebounce({
    typed_value,
    debounce,
    callback: () => {
      if (is_typing && filterable) {
        onChangeProp(typed_value);
      }
    },
  });

  useOutsideClick({
    refs: [fieldRef, optionsRef],
    callback: () => {
      setTypedValue(value);
      setOpen(false);
      resetFilter(true);
    },
  });

  useEffect(() => {
    if (!is_typing && value !== typed_value) {
      setTypedValue(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [is_typing, value]);

  const onSelect = useCallback(
    (option: T) => {
      if (filterable) {
        setTypedValue(option[option_key]);
      }
      onSelectProp(option);
    },
    [onSelectProp, filterable, option_key]
  );

  const onClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const options_filtered = useOptionsFilter({ options, option_key, typed_value, reset_filter });

  const selected_index = useMemo(
    () =>
      options_filtered.findIndex(
        (option) =>
          String(option[option_key] || '').toLowerCase() === String(typed_value).toLowerCase()
      ),
    [options_filtered, option_key, typed_value]
  );

  const { keyboard_index, setKeyboardIndex, onKeyDown } = useKeyboardNavigation<T>({
    is_open,
    selected_index,
    keyboard_disabled,
    options: options_filtered,
    onKeyDown: onKeyDownProp,
    onSelect,
    onClose,
  });

  const { is_focused, onFocus, onBlur } = useFocus({
    ref: inputRef,
    auto_focus,
    filterable,
    onFocus: onFocusProp,
    onBlur: onBlurProp,
    onOpen: () => setOpen(true),
  });

  useOutsideScroll({
    refs: [fieldRef, optionsRef],
    is_open,
    onClose: () => {
      setTypedValue(value);
      setOpen(false);
      resetFilter(true);
    },
  });

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    resetFilter(false);
    setOpen(true);
    setTypedValue(e.target.value);
  }, []);

  const onClick = useCallback(() => {
    if (filterable) {
      setKeyboardIndex(-1);
    }

    setOpen(true);
    resetFilter(true);
  }, [filterable, setKeyboardIndex]);

  const onRemove = useCallback(
    (option: T) => {
      inputRef?.current?.focus();
      onRemoveProp(option);
    },
    [onRemoveProp]
  );

  const onMouseDown = useCallback(
    (e: React.MouseEvent, position: 'left' | 'right' | 'label') => {
      e.preventDefault();
      e.stopPropagation();
      resetFilter(true);
      onMouseDownProp(e, position);

      if (position === 'label') return;
      if (position === 'left') setOpen(true);
      if (position === 'right') setOpen(!is_open);

      inputRef?.current?.focus();
    },
    [is_open, onMouseDownProp]
  );

  const synthetic_icon = useMemo(() => {
    if (right_icon) {
      return right_icon;
    }
    if (right_icon === null) {
      return undefined;
    }
    return is_open ? faChevronUp : faChevronDown;
  }, [right_icon, is_open]);

  const context = useMemo(
    () => ({
      inputRef,
      fieldRef,
      optionsRef,
      options: options_filtered,
      left_icon,
      option_key,
      placeholder,
      auto_focus,
      is_focused,
      transparent,
      label,
      information,
      height,
      width,
      padding,
      autosize,
      background,
      extend,
      value,
      prefix,
      suffix,
      filterable,
      typed_value,
      synthetic_icon,
      selected_index,
      keyboard_index,
      disabled,
      error,
      loading,
      is_open,
      clear,
      theme,
      onClick,
      onClickField,
      onMouseDown,
      onKeyDown,
      onChange,
      onBlur,
      onFocus,
      onSelect,
      onClose,
      onRemove,
    }),
    [
      fieldRef,
      optionsRef,
      options_filtered,
      left_icon,
      option_key,
      extend,
      placeholder,
      is_focused,
      transparent,
      auto_focus,
      label,
      information,
      height,
      width,
      padding,
      autosize,
      background,
      value,
      prefix,
      suffix,
      synthetic_icon,
      selected_index,
      keyboard_index,
      filterable,
      typed_value,
      disabled,
      error,
      theme,
      loading,
      is_open,
      clear,
      onClick,
      onClickField,
      onMouseDown,
      onKeyDown,
      onChange,
      onBlur,
      onFocus,
      onSelect,
      onClose,
      onRemove,
    ]
  );

  return (
    <DropdownContext.Provider value={context}>
      <div style={{ width: autosize ? 'fit-content' : width }}>{children}</div>
    </DropdownContext.Provider>
  );
};

export const DropdownField = ({ children }: { children: ReactNode }) => {
  const {
    fieldRef,
    label,
    information,
    height,
    width,
    padding,
    autosize,
    background,
    onClickField,
    onMouseDown,
    is_focused,
    left_icon,
    synthetic_icon,
    disabled,
    transparent,
    error,
    theme,
  } = useDropdownContext();

  return (
    <Field
      ref={fieldRef}
      label={label}
      information={information}
      height={height}
      width={width}
      padding={padding}
      autosize={autosize}
      background={background}
      onClick={onClickField}
      onMouseDown={onMouseDown}
      is_focused={is_focused}
      left_icon={left_icon}
      right_icon={synthetic_icon}
      disabled={disabled}
      theme={theme}
      error={error}
      transparent={transparent}
    >
      {children}
    </Field>
  );
};

export const SelectCalendarField: FC<PropsWithChildren<CalendarProps>> = ({
  value = EMPTY_ARRAY,
  mode = CALENDAR_MODE.SINGLE,
  min_date,
  max_date,
  format = 'MM/DD/YYYY',
  onSelect = EMPTY_FUNCTION,
  children,
}) => {
  const {
    fieldRef,
    label,
    information,
    height,
    width,
    padding,
    autosize,
    background,
    onClickField,
    onMouseDown,
    is_focused,
    left_icon,
    synthetic_icon,
    disabled,
    transparent,
    error,
    theme,
  } = useDropdownContext();
  return (
    <>
      <Field
        ref={fieldRef}
        label={label}
        information={information}
        height={height}
        width={width}
        padding={padding}
        autosize={autosize}
        background={background}
        onClick={onClickField}
        onMouseDown={onMouseDown}
        is_focused={is_focused}
        left_icon={left_icon}
        right_icon={synthetic_icon}
        disabled={disabled}
        theme={theme}
        error={error}
        transparent={transparent}
      >
        {children}
      </Field>
      <OptionsCalendar
        onSelect={onSelect}
        mode={mode}
        min_date={min_date}
        max_date={max_date}
        format={format}
        value={value}
      />
    </>
  );
};
